import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Upload, Trash2, ImagePlus, Clock, XCircle, MapPin } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { API } from "../lib/api";
import { Spinner } from "./common";

const SUGGESTIONS = ["Boarding pass", "Ticket", "Baggage tag", "Travel itinerary", "Extra-charges voucher", "Airport / airline photo"];

export default function PhotoGallery({ entityType, entityId, seedPhotos }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = useCallback(() => api.get("/community/photos", { params: { entity_type: entityType, entity_id: entityId } })
    .then((r) => setData(r.data)).catch(() => setData({ items: [], mine_pending: [] })), [entityType, entityId]);
  useEffect(() => { setData(null); load(); }, [load]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) { toast.error("Only JPG, PNG or WebP"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Image exceeds the 8 MB limit"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("entity_type", entityType);
    fd.append("entity_id", entityId);
    fd.append("caption", caption);
    setUploading(true);
    try {
      const { data: res } = await api.post("/community/photos", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 });
      if (res.status === "approved") toast.success("Photo published" + (res.exif_stripped ? " · hidden metadata (EXIF/GPS) removed" : ""));
      else if (res.status === "rejected") toast.error(`Rejected: ${res.reason}`);
      else toast.info("Uploaded — pending review");
      if (res.geo_tagged) toast.info(res.geo_tagged);
      if (res.privacy_warning) toast.warning(res.privacy_warning, { duration: 9000 });
      setCaption("");
      await load();
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/community/photos/${id}`); await load(); }
    catch { toast.error("Could not delete"); }
  };

  const imgUrl = (p) => `${API}${p.url.replace("/api", "")}`;

  if (!data) return <Spinner />;
  const { items, mine_pending } = data;

  return (
    <div data-testid="photo-gallery">
      {user ? (
        <div className="border border-zinc-300 bg-white p-4 mb-6">
          <div className="flex flex-wrap items-center gap-3">
            <input value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="photo-caption"
              placeholder="Add a caption (optional)" className="flex-1 min-w-[200px] border border-zinc-300 px-3 py-2 outline-none focus:ring-2 focus:ring-primary text-base" />
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onPick} className="hidden" data-testid="photo-file-input" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="photo-upload-btn"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-600 disabled:opacity-50 hover:bg-blue-700 transition-colors">
              {uploading ? <Clock className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}{uploading ? "Checking & uploading…" : "Upload photo"}
            </button>
          </div>
          <div className="text-xs text-muted-foreground mt-2">
            You can share: {SUGGESTIONS.join(" · ")}. Max 8 MB · JPG/PNG/WebP · up to 10 per page. Images are moderated before publishing.
          </div>
        </div>
      ) : (
        <div className="border border-zinc-300 bg-zinc-50 p-4 mb-6 text-sm text-muted-foreground">
          <Link to="/login" className="text-primary font-600 hover:underline">Sign in</Link> to upload photos (boarding passes, baggage tags, tickets, airport & trip photos).
        </div>
      )}

      {mine_pending?.length > 0 && (
        <div className="mb-6">
          <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Your pending / rejected uploads</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {mine_pending.map((p) => (
              <div key={p.id} className="relative border border-zinc-300 bg-white">
                <img src={imgUrl(p)} alt="" className="w-full h-32 object-cover opacity-60" />
                <div className="absolute top-1 left-1">
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-mono uppercase ${p.status === "rejected" ? "bg-red-600 text-white" : "bg-amber-500 text-white"}`}>
                    {p.status === "rejected" ? <XCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}{p.status}
                  </span>
                </div>
                <button onClick={() => del(p.id)} className="absolute top-1 right-1 bg-white/90 p-1 hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                {p.reject_reason && <div className="p-1.5 text-xs text-red-600">{p.reject_reason}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div>
          <div className="py-8 text-center text-muted-foreground">
            <ImagePlus className="w-10 h-10 mx-auto mb-2 opacity-40" />
            No community photos yet. Be the first to share one!
          </div>
          {seedPhotos?.length > 0 && (
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Travel inspiration</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {seedPhotos.map((src, i) => (
                  <div key={i} className="relative border border-zinc-300 bg-white overflow-hidden" data-testid={`seed-photo-${i}`}>
                    <img src={src} alt="" loading="lazy" className="w-full h-36 object-cover" />
                    <div className="absolute bottom-0 inset-x-0 bg-black/45 text-white text-xs p-1 text-center">Sample · upload yours</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {items.map((p) => (
            <div key={p.id} className="relative group border border-zinc-300 bg-white overflow-hidden" data-testid={`photo-${p.id}`}>
              <img src={imgUrl(p)} alt={p.caption || ""} loading="lazy" className="w-full h-36 object-cover" />
              {(user && (user.user_id === p.user_id || ["moderator", "admin"].includes(user.role))) && (
                <button onClick={() => del(p.id)} data-testid={`photo-delete-${p.id}`} className="absolute top-1 right-1 bg-white/90 p-1 opacity-0 group-hover:opacity-100 hover:text-destructive transition-opacity"><Trash2 className="w-4 h-4" /></button>
              )}
              {p.geo_place && (
                <a href={`/explore/${p.geo_slug}`} data-testid={`photo-geo-${p.id}`}
                  className="absolute top-1 left-1 inline-flex items-center gap-0.5 bg-black/55 text-white text-[10px] font-700 px-1.5 py-0.5 hover:bg-black/75 transition-colors">
                  <MapPin className="w-2.5 h-2.5" /> {p.geo_place}
                </a>
              )}
              {p.caption && <div className="absolute bottom-0 inset-x-0 bg-black/55 text-white text-xs p-1.5 line-clamp-2">{p.caption}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
